/**
 * Banner + Dialog: detector de FACTURAS DE COMPRA DUPLICADAS en Alegra
 * (mismo folio + proveedor). Iter 237 (Carlos / caso Air Liquide + PRODALAM).
 *
 * Verifica contra Alegra que las copias sigan existiendo (no da falsas alarmas
 * por copias ya borradas). Severidad:
 *   - alta : >=2 copias vivas con al menos una ABIERTA → riesgo doble pago.
 *   - media: >=2 copias vivas pero todas cerradas → posible doble pago, revisar.
 *
 * Es informativo: la anulación de la copia incorrecta se hace en Alegra.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Copy, RefreshCw, ExternalLink } from 'lucide-react';

const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

export function DuplicadosAlegraBanner({
  data,
  loading,
  open,
  onOpen,
  onClose,
  onRefresh,
}) {
  const totalGrupos = data?.total_grupos || 0;
  const totalAlta = data?.total_riesgo_alto || 0;
  if (totalGrupos === 0) return null;

  return (
    <>
      <div
        className="rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/30 dark:border-rose-800 p-3 flex items-start gap-3"
        data-testid="banner-cxp-duplicados-alegra"
      >
        <Copy className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-rose-900 dark:text-rose-200">
            {totalGrupos} factura(s) DUPLICADA(S) en Alegra
            {totalAlta > 0 && (
              <span className="ml-1 text-rose-700 dark:text-rose-300">
                · {totalAlta} con riesgo alto (copia abierta sin pagar)
              </span>
            )}
          </p>
          <p className="text-xs text-rose-800 dark:text-rose-300 mt-0.5">
            Mismo folio y proveedor aparecen 2+ veces en Alegra. Anula la copia
            incorrecta en Alegra para evitar doble pago o enlazar la factura equivocada.
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(data?.grupos || []).slice(0, 8).map((g) => (
              <span
                key={`${g.folio}-${g.proveedor_id}`}
                className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-mono ${
                  g.severidad === 'alta'
                    ? 'bg-rose-100 dark:bg-rose-900/50 text-rose-800 dark:text-rose-200'
                    : 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200'
                }`}
                title={`${g.proveedor_nombre} · ${g.n_vivas} copias`}
                data-testid={`chip-dup-${g.folio}`}
              >
                {g.folio} · {fmt(g.monto)} {g.severidad === 'alta' ? '⚠' : ''}
              </span>
            ))}
          </div>
        </div>
        <div className="flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs border-rose-500 text-rose-700 hover:bg-rose-50"
            onClick={onOpen}
            data-testid="btn-ver-duplicados-alegra"
          >
            Ver detalle
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? onOpen() : onClose())}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-duplicados-alegra">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-rose-600" />
              Facturas duplicadas en Alegra ({totalGrupos})
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {data?.verificado
                ? 'Verificado contra Alegra (copias vigentes).'
                : 'Mostrado desde caché (sin verificar contra Alegra).'}
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={onRefresh}
              disabled={loading}
              data-testid="btn-reverificar-duplicados"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Re-verificar
            </Button>
          </div>

          <div className="space-y-3 mt-2">
            {(data?.grupos || []).map((g) => (
              <div
                key={`${g.folio}-${g.proveedor_id}`}
                className="border rounded-md p-3"
                data-testid={`dup-group-${g.folio}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      Folio {g.folio} · {g.proveedor_nombre}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {g.n_vivas} copias vigentes · {g.abiertas_vivas} abierta(s)
                    </p>
                  </div>
                  <Badge
                    className={
                      g.severidad === 'alta'
                        ? 'bg-rose-600 hover:bg-rose-700'
                        : 'bg-amber-500 hover:bg-amber-600'
                    }
                  >
                    {g.severidad === 'alta' ? 'Riesgo alto' : 'Revisar'}
                  </Badge>
                </div>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="py-1 pr-2">ID Alegra</th>
                        <th className="py-1 pr-2">Fecha</th>
                        <th className="py-1 pr-2">Estado</th>
                        <th className="py-1 pr-2 text-right">Saldo</th>
                        <th className="py-1 pr-2 text-right">Total</th>
                        <th className="py-1 pr-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(g.copias || [])
                        .filter((c) => c.existe_en_alegra)
                        .map((c) => (
                          <tr key={c.alegra_id} className="border-b last:border-0">
                            <td className="py-1 pr-2 font-mono">{c.alegra_id}</td>
                            <td className="py-1 pr-2">{fmtFecha(c.fecha)}</td>
                            <td className="py-1 pr-2">
                              <span
                                className={
                                  c.saldo > 0
                                    ? 'text-rose-600 font-medium'
                                    : 'text-emerald-600'
                                }
                              >
                                {c.saldo > 0 ? 'Abierta' : 'Pagada/Cerrada'}
                              </span>
                            </td>
                            <td className="py-1 pr-2 text-right">{fmt(c.saldo)}</td>
                            <td className="py-1 pr-2 text-right">{fmt(c.total)}</td>
                            <td className="py-1 pr-2 text-right">
                              <a
                                href={`https://app.alegra.com/bill/view/id/${c.alegra_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                                title="Abrir en Alegra"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-muted-foreground mt-2">
            💡 Para corregir: entra a cada copia en Alegra y anula/elimina la
            incorrecta (normalmente la que quedó sin pago). El ERP se actualiza solo
            en el próximo sync.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
